import { Dialog } from "@mui/material";
import { Icon } from "@iconify/react";
import { useState } from "react";
import { HStack, VStack } from "../../components/utils";
import { PlanGlossaryTerm } from "../../api/Esop";
import { EsopPlan } from "../../types/EsopPlan";
import { getCurrencyType } from "../../utils/currencyFormatter";
import { getFormattedValue } from "../../utils/string";

type Props = {
  open: boolean;
  onClose: () => void;
  terms: PlanGlossaryTerm[];
  planTableData: EsopPlan[];
};

function computeTotals(planTableData: EsopPlan[]): Record<string, number> {
  return planTableData.reduce(
    (acc, plan) => ({
      poolSize: acc.poolSize + (plan.poolSize || 0),
      outstandingPool: acc.outstandingPool + (plan.outstandingPool || 0),
      totalAllocatedPool: acc.totalAllocatedPool + (plan.issuedShares || 0),
      outstandingAllocatedPool:
        acc.outstandingAllocatedPool + (plan.outstandingAllocatedPool || 0),
      stockExercised: acc.stockExercised + (plan.stockExercised || 0),
      returnedBackToPool:
        acc.returnedBackToPool + (plan.returnedBackToPool || 0),
      optionsReserved: acc.optionsReserved + (plan.optionsReserved || 0),
      optionsVested: acc.optionsVested + (plan.optionsVested || 0),
      outstandingVested: acc.outstandingVested + (plan.outstandingVested || 0),
    }),
    {
      poolSize: 0,
      outstandingPool: 0,
      totalAllocatedPool: 0,
      outstandingAllocatedPool: 0,
      stockExercised: 0,
      returnedBackToPool: 0,
      optionsReserved: 0,
      optionsVested: 0,
      outstandingVested: 0,
    }
  );
}

function resolveFormula(
  template: string,
  totals: Record<string, number>,
  currency: string,
  isFractional: boolean
): string {
  return template.replace(/\{(\w+)\}/g, (_match, fieldName) => {
    const val = totals[fieldName];
    if (val === undefined) return "0";
    return getFormattedValue(val, currency, isFractional);
  });
}

export default function PlanGlossaryModal({
  open,
  onClose,
  terms,
  planTableData,
}: Props) {
  const [selectedId, setSelectedId] = useState<string>(terms[0]?.id ?? "");
  const selected = terms.find((t) => t.id === selectedId) ?? terms[0];
  const currency = getCurrencyType();
  const isFractional = planTableData.some((p) => p.isFractional);
  const totals = computeTotals(planTableData);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <VStack className="bg-white rounded-lg overflow-hidden">
        {/* Header */}
        <HStack className="items-start justify-between px-6 pt-6 pb-4 border-b border-gray-100">
          <VStack className="gap-1">
            <HStack className="items-center gap-2">
              <Icon
                icon="material-symbols:help-outline"
                className="text-gray-500"
                width={20}
              />
              <p className="text-base font-semibold text-gray-800">
                ESOP Glossary — What Does Each Column Mean?
              </p>
            </HStack>
            <p className="text-xs text-gray-500 ml-7">
              Click on any term below to see a detailed explanation with
              examples.
            </p>
          </VStack>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <Icon icon="material-symbols:close" width={20} />
          </button>
        </HStack>

        {/* Body */}
        <HStack className="min-h-[420px]">
          {/* Left panel */}
          <VStack className="w-56 border-r border-gray-100 py-2 flex-shrink-0 overflow-y-auto max-h-[420px]">
            {terms.map((term) => (
              <button
                key={term.id}
                onClick={() => setSelectedId(term.id)}
                className={`flex items-center justify-between w-full px-4 py-3 text-left text-sm transition-colors ${
                  selectedId === term.id
                    ? "bg-gray-100 font-medium text-gray-900"
                    : "text-gray-600 hover:bg-gray-50"
                }`}
              >
                <HStack className="items-center gap-2">
                  {term.emoji && <span>{term.emoji}</span>}
                  <span>{term.name}</span>
                </HStack>
                <Icon
                  icon="material-symbols:chevron-right"
                  className="text-gray-400 flex-shrink-0"
                  width={16}
                />
              </button>
            ))}
          </VStack>

          {/* Right panel */}
          {selected && (
            <VStack className="flex-1 px-6 py-5 gap-4 overflow-y-auto max-h-[420px]">
              <HStack className="items-center gap-2">
                {selected.emoji && (
                  <span className="text-xl">{selected.emoji}</span>
                )}
                <p className="text-base font-semibold text-gray-800">
                  {selected.name}
                </p>
              </HStack>

              {/* Definition */}
              <VStack className="bg-gray-50 rounded-lg p-4 gap-1">
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                  Definition
                </p>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                  {selected.definition}
                </p>
              </VStack>

              {/* Calculation */}
              <VStack className="bg-gray-50 rounded-lg p-4 gap-1">
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                  How this number is calculated
                </p>
                <p className="text-sm text-gray-700">
                  {resolveFormula(
                    selected.formulaTemplate,
                    totals,
                    currency,
                    isFractional
                  )}
                </p>
              </VStack>
            </VStack>
          )}
        </HStack>
      </VStack>
    </Dialog>
  );
}
